import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, ShieldCheck } from 'lucide-react';
import { Logo } from '../components/Logo';
import Footer from '../components/Footer';
import { SITE_CONTENT } from '../mock';
import { LEGAL_CONTENT } from '../data/legal';
import { useLang } from '../context/LangContext';

const LegalPage = ({ kind }) => {
  const { lang, setLang } = useLang();
  const t = SITE_CONTENT[lang];
  const data = LEGAL_CONTENT[kind][lang];
  const Icon = kind === 'privacy' ? ShieldCheck : FileText;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${data.title} — Axaca Solutions Logistiques`;
  }, [data.title]);

  const backLabel = lang === 'fr' ? "Retour à l'accueil" : 'Back to home';

  return (
    <div className="bg-black min-h-screen text-white antialiased selection:bg-[#004aad] selection:text-white">
      {/* Header */}
      <header className="border-b border-white/5 bg-black/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <Logo />
          </Link>
          <div className="flex items-center gap-6">
            <div className="flex items-center text-xs text-zinc-400">
              <button
                onClick={() => setLang('fr')}
                className={`px-2 py-1 ${
                  lang === 'fr' ? 'text-white font-semibold' : 'hover:text-white'
                }`}
              >
                FR
              </button>
              <span className="text-zinc-700">|</span>
              <button
                onClick={() => setLang('en')}
                className={`px-2 py-1 ${
                  lang === 'en' ? 'text-white font-semibold' : 'hover:text-white'
                }`}
              >
                EN
              </button>
            </div>
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {backLabel}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero strip */}
      <section className="relative py-20 border-b border-white/5 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#004aad]/15 blur-[140px] rounded-full" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 mb-6">
            <Icon className="h-3.5 w-3.5 text-[#5b8def]" />
            <span className="text-[10px] tracking-[0.3em] text-zinc-300 uppercase">
              {kind === 'privacy'
                ? lang === 'fr'
                  ? 'Confidentialité'
                  : 'Privacy'
                : lang === 'fr'
                ? 'Conditions'
                : 'Terms'}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-3">
            {data.title}
          </h1>
          <p className="text-sm text-zinc-500">{data.updated}</p>
        </div>
      </section>

      {/* Content */}
      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <p className="text-lg text-zinc-300 leading-relaxed mb-12">{data.intro}</p>
        <div className="space-y-10">
          {data.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-xl md:text-2xl font-bold text-white mb-3">
                {section.heading}
              </h2>
              <p className="text-zinc-400 leading-relaxed">{section.body}</p>
            </section>
          ))}
        </div>

        <div className="mt-16 pt-10 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {backLabel}
          </Link>
          <div className="flex items-center gap-6 text-sm">
            <Link
              to="/privacy"
              className={`transition-colors ${
                kind === 'privacy'
                  ? 'text-white font-medium'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              {LEGAL_CONTENT.privacy[lang].title}
            </Link>
            <Link
              to="/terms"
              className={`transition-colors ${
                kind === 'terms'
                  ? 'text-white font-medium'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              {LEGAL_CONTENT.terms[lang].title}
            </Link>
          </div>
        </div>
      </article>

      <Footer t={t} />
    </div>
  );
};

export default LegalPage;
